/**
 * @module HeatMapManager
 * @description Manages the rendering of heatmaps for different areas and plot types in the Master Designer application.
 * This component relies on the getPlotData selector for data processing.
 */
import React, { Fragment } from "react";
import { styled } from '@mui/material/styles';
import { useSelector } from "react-redux";
import { MuiHeatMap } from "components/molecules";
import { getDrawingBox } from 'reduxModules/ducks/ui';
import { getPlotData } from 'reduxModules/selectors/containerSelectors';
import { micInCategory } from "types/models/microphone";
import type { RootState } from 'types/redux/state';

/**
 * Interface for drawing box dimensions
 */
interface DrawingBox {
  bbox: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
    cx: number;
    cy: number;
    width: number;
    height: number;
  };
  scale: number;
  zoom: boolean;
}

/**
 * Interface for area subdivision
 */
interface Subdivision {
  type: string | null;
  top: number;
  left: number;
  length: number;
  width: number;
}

/**
 * Interface for heat map data
 */
interface HeatMapData {
  id: string;
  [key: string]: any;
}

/**
 * Interface for heat map dataset
 */
interface DataSet {
  subdivision?: Subdivision;
  data: HeatMapData;
}

/**
 * Interface for area data
 */
interface Area {
  id: string;
  top: number;
  left: number;
  length: number;
  width: number;
  areaData?: DataSet[];
  dimensions?: {
    length: number;
    width: number;
  };
  location?: {
    left: number;
    top: number;
  };
}

/**
 * Interface for plot data
 */
interface PlotData {
  ready: boolean;
  areaArray: Area[];
}

/**
 * Interface for anchor position
 */
interface Anchor {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Interface for HeatMap.Wrapper props
 */
interface WrapperProps {
  id: string;
  children?: React.ReactNode;
}

/**
 * @function HeatMapManager
 * @description Renders heatmaps for each area based on the current plot data.
 * It uses the getPlotData selector to obtain processed plot information.
 * @returns The rendered HeatMapManager component
 */
const HeatMapManager: React.FC = () => {
  // Get the drawing box dimensions and scale
  const { bbox, scale, zoom } = useSelector((state: any) => getDrawingBox(state)) as DrawingBox;
  const top = bbox.y0; 
  const left = bbox.x0; 

  // Get processed plot data
  const plotData = useSelector((state: any) => getPlotData(state)) as PlotData;

  // Ceiling-tile mics (BMA360D) render an explicit coverage circle instead of the
  // micDistance heatmap, which would otherwise flood the room as a flat fill (PIG-483).
  const suppressForCeilingTile = useSelector((state: RootState) => {
    const { areas, activeTab } = state.container;
    return (
      state.ui.showMap === 'micDistance' &&
      micInCategory(areas[activeTab]?.microphones?.model, 'CEILING_TILE')
    );
  });
  if (suppressForCeilingTile) {
    return null;
  }

  if (!plotData.ready) {
    return null;
  }

  if (!plotData.areaArray || plotData.areaArray.length === 0) {
    return null;
  }

  return (
    <Fragment>
      {plotData.areaArray.map((area) => {
        // Use the exact same boundary calculation as MuiFloorplan
        // In HeatMapManager context, area has direct properties: top, left, length, width
        const { length, width } = area.dimensions || { length: area.length, width: area.width };
        const { left: areaLeft = area.left, top: areaTop = area.top } = area?.location || {};
        const { x0: OriginX, y0: OriginY } = bbox;
        
        // Calculate floorplan boundaries using the same logic as MuiFloorplan
        const floorplanX = OriginX + (!zoom ? areaLeft : 0) * scale;
        const floorplanY = OriginY + (!zoom ? areaTop : 0) * scale;
        const floorplanWidth = length * scale;
        const floorplanHeight = width * scale;

        // Calculate raw anchor point for each area using the same dimensions
        const rawTop = top + areaTop * scale * (zoom ? 0 : 1);
        const rawLeft = left + areaLeft * scale * (zoom ? 0 : 1);
        const rawWidth = length * scale;
        const rawHeight = width * scale;

        // Constrain anchor to precise floorplan boundaries (no buffer)
        const constrainedLeft = Math.max(rawLeft, floorplanX);
        const constrainedTop = Math.max(rawTop, floorplanY);
        const maxRight = floorplanX + floorplanWidth;
        const maxBottom = floorplanY + floorplanHeight;
        
        // Ensure anchor dimensions don't exceed floorplan boundaries
        const constrainedWidth = Math.min(rawWidth, maxRight - constrainedLeft);
        const constrainedHeight = Math.min(rawHeight, maxBottom - constrainedTop);

        // Final anchor with exact floorplan boundary alignment
        const anchor: Anchor = {
          top: constrainedTop,
          left: constrainedLeft,
          width: Math.max(constrainedWidth, 1),
          height: Math.max(constrainedHeight, 1),
        };

        // Skip rendering if area data is missing or dimensions are invalid
        if (!area.areaData || anchor.width <= 1 || anchor.height <= 1) {
          return null;
        }

        return (
          <HeatMapWrapper key={area.id} id={area.id}>
            {area.areaData.map((dataSet, index) => {
              const { subdivision: initialSubdivision, data } = dataSet;
              const subdivision: Subdivision = initialSubdivision ?? { 
                type: null, 
                top: 0, 
                left: 0, 
                length: 0, 
                width: 0 
              };
              const id = data.id;

              return (
                <MuiHeatMap 
                  key={id} 
                  id={id} 
                  anchor={anchor} 
                  subdivision={subdivision} 
                  data={data} 
                />
              );
            })}
          </HeatMapWrapper>
        );
      })}
    </Fragment>
  );
};

/**
 * Styled component for the HeatMap wrapper.
 */
const HeatMapWrapper = styled('div')<WrapperProps>(({ id }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  pointerEvents: 'none',
  flexGrow: 1,
  zIndex: 0, // Heatmap wrapper in background
}));

// Add the Wrapper to MuiHeatMap for compatibility with existing code
(MuiHeatMap as any).Wrapper = HeatMapWrapper;

export default HeatMapManager;
